/**
 * THE AVAILABILITY CALENDAR — which engines exist on a given date (Chris,
 * 2026-09-11: "the studio is to take these factors into consideration when
 * building an optimised timetable so you will build one that can actually run").
 *
 * An engine is OUT on a date when:
 *   - the date is before its active-from or after its active-to;
 *   - it is steam and its boiler certificate has expired;
 *   - the date falls in one of its dated out-of-service periods (exams, repairs,
 *     an overhaul — the register's `unavailable` list, db/84);
 *   - it is steam and the date falls in a WASHOUT WEEK: every `washoutCycleDays`
 *     (28) the engine is out for `washoutLockoutDays` (7) — cooling down,
 *     washing out, firing up (Chris: "lock out for a week during washout
 *     cooling and firing up time"). The washout date fixes the PHASE of the
 *     cycle; the cycle repeats before it as well as after it, so a date given
 *     for the 2027 season also places the washouts of 2026 (Chris, 2026-09-11:
 *     "is the washout plan active now?" — it was only projecting forward).
 *
 * The rest of the Studio asks one question — `engineOutOn(engine, date)` — and
 * the per-date designation (`diagrams.ts::designateDiagrams` with `{ date }`)
 * takes the engines that are in. A steam turn no steam engine can take that day
 * runs DIESEL and is marked (the railway swaps a diesel in); a turn no engine can
 * take is reported. The planner view on the Brief (stages/FleetPlannerPanel.tsx)
 * shows the same calendar week by week.
 *
 * Contract order (Chris: run a contracted engine up to its minimum before hiring
 * another per mile): `contractOrder` sorts engines so the pre-paid miles are used
 * first — offered as a button that REWRITES the register's list order, which
 * stays the one owner of pick order.
 */
import type { FleetLoco, OutPeriod, Traction } from './types'

export const WASHOUT_REASON = 'washout week — cooling down, washing out, firing up'

const DAY_MS = 86_400_000
const utc = (d: string): number => { const [y, m, dd] = d.split('-').map(Number); return Date.UTC(y, m - 1, dd) }
/** Whole days from `a` to `b` (negative when b is earlier). */
export const daysBetween = (a: string, b: string): number => Math.round((utc(b) - utc(a)) / DAY_MS)
const isDate = (s: unknown): s is string => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)

/** Why the engine cannot run on `date`, or null when it can. */
export function engineOutOn(l: FleetLoco, date: string): string | null {
  if (!isDate(date)) return null
  if (l.activeFrom && date < l.activeFrom) return `not in service until ${l.activeFrom}`
  if (l.activeTo && date > l.activeTo) return `out of service after ${l.activeTo}`
  if (l.traction === 'steam' && l.boilerCertExpiry && date > l.boilerCertExpiry) return `boiler certificate expired ${l.boilerCertExpiry}`
  for (const p of l.unavailable ?? []) {
    if (isDate(p.from) && isDate(p.to) && date >= p.from && date <= p.to) return p.reason?.trim() || 'out of service'
  }
  if (l.traction === 'steam' && isDate(l.washoutSeed ?? '') && (l.washoutCycleDays ?? 0) > 0) {
    const lock = l.washoutLockoutDays ?? 7
    const cycle = l.washoutCycleDays!
    // Days into the current cycle, counted from the washout date in either direction.
    const into = ((daysBetween(l.washoutSeed!, date) % cycle) + cycle) % cycle
    if (lock > 0 && into < lock) return WASHOUT_REASON
  }
  return null
}

/** The ticked engines that can run on `date`. */
export const availableOn = (locos: FleetLoco[] | undefined, date: string): FleetLoco[] =>
  (locos ?? []).filter((l) => l.inSimulation && !engineOutOn(l, date))

export interface DayOut { engine: FleetLoco; reason: string }
/** The ticked engines that cannot run on `date`, with why. */
export function outOn(locos: FleetLoco[] | undefined, date: string): DayOut[] {
  const out: DayOut[] = []
  for (const l of locos ?? []) {
    if (!l.inSimulation) continue
    const r = engineOutOn(l, date)
    if (r) out.push({ engine: l, reason: r })
  }
  return out
}

/** Does any ticked engine carry a date rule at all? (Without one, every date has the whole fleet and nothing per date need be recomputed.) */
export const hasCalendar = (locos: FleetLoco[] | undefined): boolean =>
  (locos ?? []).some((l) => l.inSimulation && (!!l.activeFrom || !!l.activeTo || !!l.boilerCertExpiry
    || (!!l.washoutSeed && (l.washoutCycleDays ?? 0) > 0 && (l.washoutLockoutDays ?? 7) > 0) || (l.unavailable?.length ?? 0) > 0))

/**
 * Contract rank — lower runs first:
 *   0 a contracted MINIMUM (its miles are paid whether run or not — use them);
 *     bigger minimum first
 *   1 owned, no hire
 *   2 per-mile hire with no minimum — cheapest first
 *   3 a day rate only
 *   4 anything else
 */
export function contractRank(l: FleetLoco): [number, number] {
  if ((l.minContractMiles ?? 0) > 0) return [0, -(l.minContractMiles ?? 0)]
  if (l.ownership === 'nymr' && !(l.costPerMile ?? 0) && !(l.dayRate ?? 0)) return [1, 0]
  if ((l.costPerMile ?? 0) > 0) return [2, l.costPerMile ?? 0]
  if ((l.dayRate ?? 0) > 0) return [3, l.dayRate ?? 0]
  return [4, 0]
}
/** Engines in contract order, ties in their current list order. */
export function contractOrder(locos: FleetLoco[]): FleetLoco[] {
  return [...locos].sort((a, b) => {
    const [ra, ka] = contractRank(a), [rb, kb] = contractRank(b)
    return (ra - rb) || (ka - kb) || (a.sortOrder - b.sortOrder) || (a.id - b.id)
  })
}

/** ISO Monday of the week a date falls in. */
export function weekStart(date: string): string {
  const t = utc(date)
  const dow = (new Date(t).getUTCDay() + 6) % 7
  return new Date(t - dow * DAY_MS).toISOString().slice(0, 10)
}

export interface AvailabilityWeek { start: string; label: string; dates: string[] }
export interface AvailabilityRow { engine: FleetLoco; cells: { out: number; reasons: string[] }[] }
export interface AvailabilityGrid {
  weeks: AvailabilityWeek[]
  rows: AvailabilityRow[]
  /** Per week, the FEWEST engines of each traction available on any running day. */
  available: Record<Traction, number[]>
}
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
/** The planner's week grid over the running dates given: each ticked engine's out days per week, with reasons, and the worst-day availability per traction. */
export function availabilityGrid(locos: FleetLoco[] | undefined, runningDates: string[]): AvailabilityGrid {
  const use = (locos ?? []).filter((l) => l.inSimulation)
  const dates = [...new Set(runningDates.filter(isDate))].sort()
  const byWeek = new Map<string, string[]>()
  for (const d of dates) { const w = weekStart(d); byWeek.set(w, [...(byWeek.get(w) ?? []), d]) }
  const weeks: AvailabilityWeek[] = [...byWeek.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([start, ds]) => {
    const [, m, dd] = start.split('-').map(Number)
    return { start, label: `${dd} ${MON[m - 1]}`, dates: ds }
  })
  const rows: AvailabilityRow[] = use.map((engine) => ({
    engine,
    cells: weeks.map((w) => {
      const reasons: string[] = []
      let out = 0
      for (const d of w.dates) { const r = engineOutOn(engine, d); if (r) { out += 1; if (!reasons.includes(r)) reasons.push(r) } }
      return { out, reasons }
    }),
  }))
  const available: Record<Traction, number[]> = { steam: [], diesel: [], railcar: [] }
  for (const w of weeks) {
    for (const t of ['steam', 'diesel', 'railcar'] as Traction[]) {
      let min = Infinity
      for (const d of w.dates) min = Math.min(min, availableOn(use, d).filter((l) => l.traction === t).length)
      available[t].push(Number.isFinite(min) ? min : 0)
    }
  }
  return { weeks, rows, available }
}

/** A saved list of periods made safe: both dates present and in order, reason trimmed. */
export function normalisePeriods(raw: unknown): OutPeriod[] {
  if (!Array.isArray(raw)) return []
  const out: OutPeriod[] = []
  for (const p of raw as Partial<OutPeriod>[]) {
    if (!isDate(p?.from) || !isDate(p?.to)) continue
    const [from, to] = p.from! <= p.to! ? [p.from!, p.to!] : [p.to!, p.from!]
    out.push({ from, to, reason: String(p.reason ?? '').trim().slice(0, 120) })
  }
  return out.sort((a, b) => a.from.localeCompare(b.from))
}
